/**
 * Every product module's pages, as data: what the ⌘K bar offers for a module now that
 * "New navigation" no longer draws product sections in the sidebar (v3: Products hidden),
 * so a page that lives only in a module (Copilot posture, Gemini settings, Endpoint
 * policy…) is still one search away.
 *
 * Each spec lists the SAME pages the module's classic section links, with the classic
 * row's gate as `when(ctx)`; modulePages.test.jsx fails if a module gains or loses a link.
 * Specs are grouped under the pillar headings (MODULE_PILLARS) and hang related pages under
 * the page they belong to (`children`).
 *
 * Item: { to, icon, labelKey (namespaced) | label, when?, badge?, children? }
 * ctx:  { can(module), cap(capability), isAdmin, canGov, canAlerts, featureOn(flag) }
 */
import {
  CUSTOM_AGENTS_DASHBOARD_PATH,
  GEMINI_ENTERPRISE_DASHBOARD_LABEL,
  GEMINI_ENTERPRISE_DASHBOARD_PATH,
  GEMINI_WORKSPACE_DASHBOARD_LABEL,
  GEMINI_WORKSPACE_DASHBOARD_PATH,
} from './productConfig';
import { getCloudDiscoveryNavGroups, getCopilotNavGroups, getSaasAiNavGroups } from './copilotNavConfig';

export const MODULE_PILLARS = ['discovery', 'assessment', 'security', 'usage'];

const P = (key) => `nav:pillarItems.${key}`;
const I = (key) => `nav:items.${key}`;
const L = (key) => `layout:sidebarSidebar.${key}`;

/** The shared Atlas security pages pinned under `base` (/x/security/*). */
const securityPages = (base, when) => ({
  detections: { to: `${base}/signals`, icon: 'activity', labelKey: P('detections'), when },
  incidents: {
    to: `${base}/incidents`, icon: 'alert-octagon', labelKey: P('incidents'), when,
    children: [{ to: `${base}/response`, icon: 'zap', labelKey: P('responseApprovals'), when }],
  },
  hunt: { to: `${base}/hunt`, icon: 'search', labelKey: P('threatHunting'), when },
  skills: { to: `${base}/skills`, icon: 'layers', labelKey: P('skillsWorkflows'), when },
  exceptions: { to: `${base}/exceptions`, icon: 'slash', labelKey: P('exceptions'), when },
  aiBom: { to: `${base}/ai-bom`, icon: 'package', labelKey: P('aiBom'), when },
});

// ── Modules drawn from copilotNavConfig's shared groups (Cloud, M365 Copilot,
//    Copilot Studio, AI Providers) ─────────────────────────────────────────────
const GROUPED_PILLAR = {
  activationDashboard: 'top',
  discoveryDashboard: 'top',
  recommendations: 'top',
  agentRegistry: 'discovery',
  users: 'discovery',
  identities: 'discovery',
  agentNetwork: 'discovery',
  inventory: 'discovery',
  knowledgeBases: 'discovery',
  sourceIntelligence: 'discovery',
  aiBomInventory: 'discovery',
  accessManagement: 'assessment',
  posture: 'assessment',
  exceptions: 'security',
  signals: 'security',
  incidents: 'security',
  hunt: 'security',
  skills: 'security',
  usageAndRoi: 'usage',
  conversations: 'usage',
  knowledgeGaps: 'usage',
  intentAnalysis: 'usage',
};
const GROUPED_RENAME = {
  signals: 'detections',
  hunt: 'threatHunting',
  skills: 'skillsWorkflows',
  response: 'responseApprovals',
  knowledgeGaps: 'unansweredQuestions',
  intentAnalysis: 'topicsIntents',
  aiBomInventory: 'aiBom',
  policy: 'policyRules',
  alerts: 'notificationRules',
};
const groupedItem = (navItem) => ({
  to: navItem.to,
  icon: navItem.icon,
  ...(navItem.label ? { label: navItem.label }
    : { labelKey: GROUPED_RENAME[navItem.labelKey] ? P(GROUPED_RENAME[navItem.labelKey]) : I(navItem.labelKey) }),
});
const SECURITY_ORDER = ['signals', 'incidents', 'hunt', 'skills'];

/** copilotNavConfig groups → a pillar spec. Every page the groups hold, once. */
export const pillarizeGroups = (groups) => {
  const spec = { top: [], discovery: [], assessment: [], security: [], usage: [] };
  const all = [...groups.overview, ...groups.adoption, ...groups.security];
  const response = groups.security.find((it) => it.labelKey === 'response');
  all.forEach((it) => {
    if (it === response) return;
    const pillar = GROUPED_PILLAR[it.labelKey];
    if (!pillar) throw new Error(`modulePillarNav: no pillar for ${it.labelKey} (${it.to})`);
    const entry = groupedItem(it);
    if (it.labelKey === 'incidents' && response) entry.children = [groupedItem(response)];
    spec[pillar].push(entry);
  });
  spec.security.sort((a, b) => SECURITY_ORDER.findIndex((k) => a.to.endsWith(`/${k}`))
    - SECURITY_ORDER.findIndex((k) => b.to.endsWith(`/${k}`)));
  spec.governance = {
    compliance: { compliance: groups.compliance.to, auditReport: groups.auditReport ? groups.auditReport.to : null },
    logExplorer: groups.logExplorer.to,
    items: [groups.policy, groups.notifications, groups.settings].filter(Boolean).map(groupedItem),
  };
  return spec;
};

// ── Modules with hand-built sections ──────────────────────────────────────────
const always = undefined;

const customAgents = () => {
  const security = (c) => c.can('security');
  const sec = securityPages('/custom-agents/security', security);
  return {
    top: [
      { to: CUSTOM_AGENTS_DASHBOARD_PATH, icon: 'grid', labelKey: L('dashboard'), when: (c) => c.can('monitoring') },
      {
        to: '/custom-agents/recommendations', icon: 'inbox', labelKey: I('recommendations'), badge: 'customAgents',
        when: (c) => c.cap('recommendations') && (c.can('monitoring') || c.can('security')),
      },
    ],
    discovery: [
      { to: '/custom-agents/registry', icon: 'list', labelKey: L('agentRegistry'), when: (c) => c.isAdmin || c.canGov },
      { to: '/user-behavior', icon: 'users', labelKey: P('usersIdentities'), when: (c) => c.can('monitoring') },
      { to: '/agent-network', icon: 'share-2', labelKey: L('agentNetwork'), when: (c) => c.can('monitoring') },
      { to: '/custom-agents/sources', icon: 'database', labelKey: L('sourceIntelligence'), when: security },
      sec.aiBom,
    ],
    assessment: [
      {
        to: '/custom-agents/assessment', icon: 'list', labelKey: P('findings'), when: (c) => c.canGov,
        children: [
          { to: '/red-team-scanner', icon: 'shield', labelKey: P('redTeam'), when: (c) => c.canGov },
          { to: '/red-team/scheduled-scans', icon: 'clock', labelKey: P('scheduledRuns'), when: (c) => c.canGov },
          { to: '/model-evaluation/projects', icon: 'box', labelKey: P('modelEvaluation'), when: (c) => c.canGov },
        ],
      },
    ],
    security: [
      { to: '/custom-agents/security/overview', icon: 'shield', labelKey: P('threatSummary'), when: security },
      {
        ...sec.detections,
        children: [{ to: '/custom-agents/security/data-security', icon: 'database', labelKey: P('sensitiveData'), when: security }],
      },
      sec.incidents,
      sec.hunt,
      sec.skills,
      sec.exceptions,
    ],
    usage: [
      { to: '/custom-agents/finops', icon: 'dollar-sign', labelKey: P('aiSpend'), when: (c) => c.can('finops') },
      { to: '/custom-agents/conversations', icon: 'message-circle', labelKey: L('conversations'), when: (c) => c.can('monitoring') },
      { to: '/custom-agents/knowledge-gaps', icon: 'help-circle', labelKey: P('unansweredQuestions'), when: (c) => c.can('monitoring') },
      { to: '/intent-analysis', icon: 'target', labelKey: P('topicsIntents'), when: (c) => c.can('monitoring') && c.cap('intentAnalysis') },
      { to: '/observability', icon: 'activity', labelKey: L('observability'), when: (c) => c.can('monitoring') },
    ],
    governance: {
      compliance: { compliance: '/admin/compliance', auditReport: '/admin/audit-report', when: (c) => c.can('compliance') },
      logExplorer: '/security/audit-log',
      logExplorerWhen: security,
      items: [
        { to: '/admin/policies', icon: 'shield', labelKey: P('policyAndGuardrails'), when: (c) => c.canGov || c.can('security') },
        { to: '/admin/custom-guardrails', icon: 'sliders', labelKey: L('customGuardrails'), when: security },
        { to: '/observability/alerts', icon: 'bell', labelKey: P('notificationRules'), when: (c) => c.canAlerts },
      ],
    },
  };
};

const endpointSecurity = () => {
  const sec = securityPages('/endpoint-security/security', always);
  return {
    top: [{ to: '/endpoint-security', icon: 'grid', labelKey: I('dashboard') }],
    discovery: [
      { to: '/endpoint-security/endpoints', icon: 'monitor', labelKey: I('endpoints') },
      { to: '/endpoint-security/users', icon: 'users', labelKey: P('usersIdentities') },
      // Literal in the classic row too.
      { to: '/endpoint-security/tool-catalog', icon: 'package', label: 'AI app catalogue' },
      sec.aiBom,
      { to: '/endpoint-security/coverage', icon: 'target', labelKey: I('coverage') },
    ],
    assessment: [],
    security: [
      {
        ...sec.detections,
        children: [
          { to: '/endpoint-security/dlp-analytics', icon: 'shield', labelKey: I('dlpAnalytics') },
          { to: '/endpoint-security/guardrails', icon: 'filter', labelKey: I('guardrailDetections') },
        ],
      },
      sec.incidents,
      sec.hunt,
      sec.skills,
      sec.exceptions,
    ],
    usage: [
      { to: '/endpoint-security/adoption/conversations', icon: 'message-square', labelKey: L('conversations') },
      { to: '/endpoint-security/adoption/knowledge-gaps', icon: 'help-circle', labelKey: P('unansweredQuestions') },
      { to: '/endpoint-security/adoption/intent-analysis', icon: 'target', labelKey: P('topicsIntents') },
    ],
    governance: {
      compliance: { compliance: '/endpoint-security/compliance', auditReport: '/endpoint-security/audit-report' },
      logExplorer: '/endpoint-security/log-explorer',
      items: [
        { to: '/endpoint-security/policy', icon: 'lock', labelKey: P('policyRules') },
        { to: '/endpoint-security/alerts', icon: 'bell', labelKey: P('notificationRules') },
      ],
    },
  };
};

const codeSecurity = () => {
  const sec = securityPages('/sast/security', always);
  return {
    top: [
      { to: '/sast/overview', icon: 'grid', labelKey: L('dashboard') },
      { to: '/sast/recommendations', icon: 'inbox', labelKey: L('recommendations'), badge: 'codeSecurity', when: (c) => c.featureOn('RECOMMENDATIONS') },
    ],
    discovery: [
      { to: '/sast/repositories', icon: 'git-branch', labelKey: L('repositories') },
      sec.aiBom,
    ],
    // Code findings are findings: they lead Assessment here.
    assessment: [
      { to: '/sast/findings', icon: 'alert-circle', labelKey: L('findings') },
    ],
    security: [sec.detections, sec.incidents, sec.hunt, sec.skills, sec.exceptions],
    usage: [],
    governance: {
      // No Audit Report and no Log Explorer yet (see the classic section).
      compliance: { compliance: '/sast/compliance', auditReport: null },
      logExplorer: null,
      items: [
        { to: '/sast/policy', icon: 'lock', labelKey: P('policyRules') },
        { to: '/sast/settings', icon: 'settings', labelKey: L('settings') },
      ],
    },
  };
};

const geminiEnterprise = () => {
  const sec = securityPages('/gemini/agents/security', always);
  return {
    top: [
      { to: GEMINI_ENTERPRISE_DASHBOARD_PATH, icon: 'grid', label: GEMINI_ENTERPRISE_DASHBOARD_LABEL },
      { to: '/gemini/agents/recommendations', icon: 'zap', labelKey: L('recommendations') },
    ],
    discovery: [
      { to: '/gemini/agents/registry', icon: 'server', labelKey: L('agentRegistry') },
      { to: '/gemini/agents/users', icon: 'users', labelKey: P('usersIdentities') },
      { to: '/gemini/agents/agent-network', icon: 'share-2', labelKey: L('agentNetwork') },
      { to: '/gemini/agents/sources', icon: 'database', labelKey: I('sourceIntelligence') },
      sec.aiBom,
    ],
    assessment: [
      { to: '/gemini/agents/posture', icon: 'lock', labelKey: I('accessManagement') },
    ],
    security: [sec.detections, sec.incidents, sec.hunt, sec.skills, sec.exceptions],
    usage: [
      { to: '/gemini/agents/usage-roi', icon: 'trending-up', labelKey: I('usageAndRoi') },
      { to: '/gemini/agents/conversations', icon: 'message-circle', labelKey: I('conversations') },
      { to: '/gemini/agents/knowledge-gaps', icon: 'book-open', labelKey: P('unansweredQuestions') },
      { to: '/gemini/agents/intent-analysis', icon: 'target', labelKey: P('topicsIntents') },
    ],
    governance: {
      compliance: { compliance: '/gemini/agents/compliance', auditReport: '/gemini/agents/audit-report' },
      logExplorer: '/gemini/agents/log-explorer',
      items: [
        { to: '/gemini/agents/policy', icon: 'shield', labelKey: P('policyRules'), when: (c) => c.featureOn('GEMINI_ENTERPRISE_POLICY') },
        { to: '/gemini/agents/alerts', icon: 'bell', labelKey: P('notificationRules') },
        { to: '/gemini/agents/settings', icon: 'settings', labelKey: L('settings') },
      ],
    },
  };
};

const geminiWorkspace = () => ({
  top: [{ to: GEMINI_WORKSPACE_DASHBOARD_PATH, icon: 'grid', label: GEMINI_WORKSPACE_DASHBOARD_LABEL }],
  discovery: [
    { to: '/gemini/users', icon: 'users', labelKey: P('usersIdentities') },
    { to: '/gemini/applications', icon: 'grid', labelKey: L('workspaceApps') },
  ],
  assessment: [],
  security: [{ to: '/gemini/security/signals', icon: 'alert-triangle', labelKey: P('detections') }],
  usage: [
    { to: '/gemini/usage-roi', icon: 'trending-up', labelKey: L('usageRoi') },
    { to: '/gemini/intent-analysis', icon: 'target', labelKey: P('topicsIntents') },
  ],
  governance: {
    compliance: { compliance: '/gemini/compliance', auditReport: '/gemini/audit-report' },
    logExplorer: '/gemini/log-explorer',
    items: [
      { to: '/gemini/alerts', icon: 'bell', labelKey: P('notificationRules') },
      { to: '/gemini/settings', icon: 'settings', labelKey: L('settings') },
    ],
  },
});

/** Pillar specs for the hand-built module sections, by sidebar section key. */
export const HAND_BUILT_MODULE_PILLARS = {
  'custom-agents': customAgents,
  'shadow-ai': endpointSecurity,
  sast: codeSecurity,
  'gemini-enterprise-agents': geminiEnterprise,
  'gemini-workspace': geminiWorkspace,
};

/** Every route a spec links (for tests and active-section checks). */
export const specRoutes = (spec) => [
  ...spec.top,
  ...MODULE_PILLARS.flatMap((p) => spec[p] || []),
  ...spec.governance.items,
].flatMap((it) => [it.to, ...(it.children || []).map((c) => c.to)])
  .concat([spec.governance.compliance?.compliance, spec.governance.compliance?.auditReport, spec.governance.logExplorer])
  .filter(Boolean);

/** Modules drawn from copilotNavConfig's shared groups, by sidebar section key. */
export const GROUPED_MODULES = {
  'cloud-ai': getCloudDiscoveryNavGroups,
  'enterprise-ai': () => getCopilotNavGroups(false),
  'copilot-studio': () => getCopilotNavGroups(true),
  'saas-ai-platform': getSaasAiNavGroups,
};

/** A module's spec by sidebar section key, or null. */
export const moduleSpec = (sectionKey) => {
  if (HAND_BUILT_MODULE_PILLARS[sectionKey]) return HAND_BUILT_MODULE_PILLARS[sectionKey]();
  if (GROUPED_MODULES[sectionKey]) return pillarizeGroups(GROUPED_MODULES[sectionKey]());
  return null;
};

/**
 * The pages of one module `ctx` may open, flat: [{ to, labelKey | label }]. A child whose
 * parent is gated out still counts on its own gate. Governance's compliance pages and Log
 * Explorer come with their Atlas labels' keys.
 */
export const modulePages = (spec, ctx) => {
  const shown = (it) => !it.when || it.when(ctx);
  const pages = [];
  const take = (it) => { if (shown(it)) pages.push({ to: it.to, labelKey: it.labelKey, label: it.label }); };
  [...spec.top, ...MODULE_PILLARS.flatMap((p) => spec[p] || []), ...spec.governance.items].forEach((it) => {
    take(it);
    (it.children || []).forEach(take);
  });
  const gov = spec.governance;
  if (gov.compliance && shown(gov.compliance)) {
    pages.push({ to: gov.compliance.compliance, labelKey: 'nav:items.compliance' });
    if (gov.compliance.auditReport) pages.push({ to: gov.compliance.auditReport, labelKey: 'nav:items.auditReport' });
  }
  if (gov.logExplorer && (!gov.logExplorerWhen || gov.logExplorerWhen(ctx))) {
    pages.push({ to: gov.logExplorer, labelKey: 'nav:items.logExplorer' });
  }
  return pages;
};
